/**
 * Standalone WYSIWYG Note Editor Modal
 * Can be triggered from Drive (doc list or file list folders)
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import DiffMatchPatch from 'diff-match-patch';
import { http } from '@/lib/http';
import { useSession } from '@/features/auth/hooks/useSession';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { completeNote } from '@/lib/api/endpoints/chat';
import { parseDocEditResponse } from './docEditCommand.utils';
import { describeVersionRow } from './docVersionRow';
import { useDocSaveFailureBuffer, readUnsavedDocStash, clearUnsavedDocStash } from './useDocSaveFailureBuffer';
import { adjudicateDocStash, deriveTitle, ensureHtmlLineBreaks, type DocCheckpoint } from './docStashAdjudication';
import { useToast } from '@/app/providers/ToastContext';
import '../../_infrastructure/ModuleStyles.css';

// ---------- Types ----------
// DocCheckpoint (version-history checkpoint) lives in docStashAdjudication.ts.

export interface DocForEditor {
  id: string;
  title?: string;
  body?: string;
  content?: string;
  _etag?: string;
  authorId?: string;
  checkpoints?: DocCheckpoint[];
}

interface DocEditorModalProps {
  doc: DocForEditor;
  onClose: () => void;
}

// ---------- Helpers ----------

function formatSaveStatus(date: Date | null): string {
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  if (diffSecs < 10) return 'Saved just now';
  if (diffSecs < 60) return `Saved ${diffSecs}s ago`;
  if (diffMins < 60) return `Saved ${diffMins}m ago`;
  return `Saved ${Math.floor(diffMins / 60)}h ago`;
}

// ensureHtmlLineBreaks moved to docStashAdjudication.ts (shared with the stash adjudicator).

// ---------- Constants ----------

const FONTS = [
  { name: 'Lora', value: 'Lora, Georgia, serif' },
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Times', value: 'Times New Roman, serif' },
  { name: 'Arial', value: 'Arial, sans-serif' },
  { name: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { name: 'Verdana', value: 'Verdana, sans-serif' },
  { name: 'Courier', value: 'Courier New, monospace' },
];
const FONT_SIZES = ['12', '14', '16', '17', '18', '20', '24', '28', '32'];
const TEXT_COLORS = [
  { name: 'Black', color: 'var(--color-text)' },
  { name: 'Gray', color: 'var(--color-text-muted)' },
  { name: 'Red', color: 'var(--color-danger)' },
  { name: 'Orange', color: 'var(--color-warning-strong)' },
  { name: 'Yellow', color: '#ca8a04' },
  { name: 'Green', color: 'var(--color-success)' },
  { name: 'Blue', color: 'var(--color-primary-hover)' },
  { name: 'Purple', color: '#9333ea' },
  { name: 'Pink', color: '#db2777' },
];

// ---------- FontSize extension ----------

const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.fontSize || null,
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}` };
        },
      },
    };
  },
});

// ---------- Diff Utilities ----------

function generateDiff(oldContent: string, newContent: string): { html: string; stats: { additions: number; deletions: number } } {
  const dmp = new DiffMatchPatch();
  const diffs = dmp.diff_main(oldContent, newContent);
  dmp.diff_cleanupSemantic(diffs);

  let html = '';
  let additions = 0;
  let deletions = 0;

  diffs.forEach(([op, text]) => {
    if (op === 1) {
      html += `<ins style="background: #dcfce7; text-decoration: none; padding: 0px 2px;">${text}</ins>`;
      additions += text.length;
    } else if (op === -1) {
      html += `<del style="background: #fee2e2; text-decoration: line-through; padding: 0px 2px;">${text}</del>`;
      deletions += text.length;
    } else {
      html += text;
    }
  });

  return { html, stats: { additions, deletions } };
}

// Diff View Component for Version History
function DiffView({ oldContent, newContent }: { oldContent: string; newContent: string }) {
  const diff = generateDiff(oldContent, newContent);

  const extractFont = (html: string): string => {
    const fontMatch = html.match(/font-family:\s*([^;"]+)/);
    return fontMatch ? fontMatch[1] : 'Lora, Georgia, serif';
  };

  const extractFontSize = (html: string): string => {
    const sizeMatch = html.match(/font-size:\s*(\d+)px/);
    return sizeMatch ? sizeMatch[1] : '17';
  };

  const currentFont = extractFont(oldContent);
  const currentSize = extractFontSize(oldContent);

  return (
    <div className="wysiwyg-editor">
      <div className="wysiwyg-toolbar" style={{ opacity: 0.5, pointerEvents: 'none' }}>
        <select className="toolbar-select toolbar-font-select" disabled style={{ fontFamily: currentFont }} value={currentFont}>
          {FONTS.map(({ name, value }) => (
            <option key={value} value={value} style={{ fontFamily: value }}>{name}</option>
          ))}
        </select>
        <select className="toolbar-select toolbar-size-select" disabled value={currentSize}>
          {FONT_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
        </select>
        <div className="color-picker-wrapper">
          <button type="button" className="toolbar-btn color-btn" disabled title="Text Color">
            <span style={{ borderBottom: '3px solid #000' }}>A</span>
          </button>
        </div>
        <div className="toolbar-divider" />
        <button type="button" className="toolbar-btn" disabled title="Bold"><strong>B</strong></button>
        <button type="button" className="toolbar-btn" disabled title="Italic"><em>I</em></button>
        <button type="button" className="toolbar-btn" disabled title="Underline"><span style={{ textDecoration: 'underline' }}>U</span></button>
        <button type="button" className="toolbar-btn" disabled title="Strikethrough"><span style={{ textDecoration: 'line-through' }}>S</span></button>
        <div className="toolbar-align-group">
          <div className="toolbar-divider" />
          <button type="button" className="toolbar-btn" disabled title="Align Left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="1" y="2" width="14" height="2" /><rect x="1" y="6" width="10" height="2" />
              <rect x="1" y="10" width="14" height="2" /><rect x="1" y="14" width="8" height="2" /></svg></button>
          <button type="button" className="toolbar-btn" disabled title="Align Center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="1" y="2" width="14" height="2" /><rect x="3" y="6" width="10" height="2" />
              <rect x="1" y="10" width="14" height="2" /><rect x="4" y="14" width="8" height="2" /></svg></button>
          <button type="button" className="toolbar-btn" disabled title="Align Right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <rect x="1" y="2" width="14" height="2" /><rect x="5" y="6" width="10" height="2" />
              <rect x="1" y="10" width="14" height="2" /><rect x="7" y="14" width="8" height="2" /></svg></button>
        </div>
      </div>
      <div
        className="ProseMirror xv-scroll"
        style={{
          padding: '24px 32px',
          background: 'var(--color-surface)',
          overflowY: 'auto',
          height: '100%',
          minHeight: '400px',
          outline: 'none',
          fontFamily: currentFont,
          fontSize: `${currentSize}px`,
          lineHeight: '1.7',
          flex: '1 1 0',
          maxHeight: '100%',
          color: 'var(--color-text)',
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: diff.html }} />
      </div>
    </div>
  );
}

// ---------- Toolbar ----------

function ToolbarDropdown({ value, options, onChange, width, renderLabel }: {
  value: string;
  options: { label: string; value: string; style?: React.CSSProperties }[];
  onChange: (value: string) => void;
  width: number;
  renderLabel?: (value: string) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selected = options.find(o => o.value === value);
  return (
    <div ref={ref} style={{ position: 'relative', width }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="toolbar-select"
        style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...(selected?.style || {}) }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {renderLabel ? renderLabel(value) : selected?.label || value}
        </span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" style={{ flexShrink: 0, marginLeft: 4, opacity: 0.5 }}>
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="xv-scroll" style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, width: Math.max(width, 140),
          background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8,
          boxShadow: 'var(--popover-shadow, 0 4px 16px rgba(0,0,0,0.12))', zIndex: 100, maxHeight: 240, overflowY: 'auto',
          padding: '4px 0',
        }}>
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px',
                border: 'none', background: opt.value === value ? 'var(--color-surface-hover, #f3f4f6)' : 'transparent',
                cursor: 'pointer', fontSize: 13, color: 'var(--color-text)',
                fontWeight: opt.value === value ? 600 : 400,
                ...opt.style,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-hover, #f3f4f6)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = opt.value === value ? 'var(--color-surface-hover, #f3f4f6)' : 'transparent'; }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EditorToolbar({ editor, onToggleAI, isAIOpen, onToggleVersionHistory, isVersionHistoryOpen, checkpointCount, onSaveVersion }: { editor: ReturnType<typeof useEditor>; onToggleAI?: () => void; isAIOpen?: boolean; onToggleVersionHistory?: () => void; isVersionHistoryOpen?: boolean; checkpointCount?: number; onSaveVersion?: () => void }) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  if (!editor) return null;
  const currentFont = editor.getAttributes('textStyle').fontFamily || 'Lora, Georgia, serif';
  const currentSize = editor.getAttributes('textStyle').fontSize?.replace('px', '') || '17';

  return (
    <div className="wysiwyg-toolbar">
      <ToolbarDropdown
        value={currentFont}
        options={FONTS.map(({ name, value }) => ({ label: name, value, style: { fontFamily: value } }))}
        onChange={(v) => editor.chain().focus().setFontFamily(v).run()}
        width={110}
        renderLabel={(v) => <span style={{ fontFamily: v }}>{FONTS.find(f => f.value === v)?.name || 'Font'}</span>}
      />
      <ToolbarDropdown
        value={currentSize}
        options={FONT_SIZES.map(size => ({ label: String(size), value: String(size) }))}
        onChange={(v) => editor.chain().focus().setMark('textStyle', { fontSize: `${v}px` }).run()}
        width={55}
      />
      <div className="color-picker-wrapper">
        <button type="button" className="toolbar-btn color-btn"
          onClick={() => setShowColorPicker(!showColorPicker)} title="Text Color">
          <span style={{ borderBottom: `3px solid ${editor.getAttributes('textStyle').color || '#000'}` }}>A</span>
        </button>
        {showColorPicker && (
          <div className="color-picker-dropdown">
            {TEXT_COLORS.map(({ name, color }) => (
              <button key={color} type="button"
                className={`color-swatch ${(editor.getAttributes('textStyle').color || 'var(--color-text)').toLowerCase() === color.toLowerCase() ? 'active' : ''}`}
                style={{ backgroundColor: color }} title={name}
                onClick={() => { editor.chain().focus().unsetColor().setColor(color).run(); setShowColorPicker(false); }} />
            ))}
          </div>
        )}
      </div>
      <div className="toolbar-divider" />
      <button type="button" className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
        onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><strong>B</strong></button>
      <button type="button" className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
        onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><em>I</em></button>
      <button type="button" className={`toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`}
        onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
        <span style={{ textDecoration: 'underline' }}>U</span></button>
      <button type="button" className={`toolbar-btn ${editor.isActive('strike') ? 'active' : ''}`}
        onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
        <span style={{ textDecoration: 'line-through' }}>S</span></button>
      <div className="toolbar-align-group">
        <div className="toolbar-divider" />
        <button type="button" className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
          onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align Left">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="1" y="2" width="14" height="2" /><rect x="1" y="6" width="10" height="2" />
            <rect x="1" y="10" width="14" height="2" /><rect x="1" y="14" width="8" height="2" /></svg></button>
        <button type="button" className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
          onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align Center">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="1" y="2" width="14" height="2" /><rect x="3" y="6" width="10" height="2" />
            <rect x="1" y="10" width="14" height="2" /><rect x="4" y="14" width="8" height="2" /></svg></button>
        <button type="button" className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
          onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align Right">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="1" y="2" width="14" height="2" /><rect x="5" y="6" width="10" height="2" />
            <rect x="1" y="10" width="14" height="2" /><rect x="7" y="14" width="8" height="2" /></svg></button>
      </div>
      {(onToggleAI || onToggleVersionHistory || onSaveVersion) && (
        <>
          <div style={{ flex: 1 }} />
          {onSaveVersion && (
            <button type="button"
              onClick={onSaveVersion}
              title="Save version"
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--color-text-muted)', borderRadius: '6px', transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-subtle)'; e.currentTarget.style.background = 'var(--color-surface-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.background = 'none'; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
          )}
          {onToggleVersionHistory && (
            <button type="button"
              onClick={onToggleVersionHistory}
              title={isVersionHistoryOpen ? "Close version history" : `Version history${(checkpointCount ?? 0) > 0 ? ` (${checkpointCount})` : ''}`}
              style={{
                background: isVersionHistoryOpen ? 'var(--color-primary-muted)' : 'none',
                border: 'none', cursor: 'pointer', padding: '6px 8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                color: isVersionHistoryOpen ? 'var(--color-primary)' : 'var(--color-text-muted)',
                borderRadius: '6px', transition: 'all 0.15s', fontSize: '16px', fontWeight: 400,
              }}
              onMouseEnter={(e) => { if (!isVersionHistoryOpen) { e.currentTarget.style.color = 'var(--color-text-subtle)'; e.currentTarget.style.background = 'var(--color-surface-hover)'; } }}
              onMouseLeave={(e) => { if (!isVersionHistoryOpen) { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.background = 'none'; } }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
                <path d="M12 7v5l3 3"/>
              </svg>
            </button>
          )}
          {onToggleAI && (
            <button type="button" className={`toolbar-btn ${isAIOpen ? 'active' : ''}`}
              onClick={onToggleAI} title={isAIOpen ? 'Close AI panel' : 'Open AI panel'}
              style={{ color: isAIOpen ? 'var(--color-primary, #1a9ba5)' : undefined }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="15" y1="3" x2="15" y2="21" />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Tiptap Editor ----------

function WysiwygEditor({ content, onChange, placeholder, editable = true, onToggleAI, isAIOpen, onToggleVersionHistory, isVersionHistoryOpen, checkpointCount, onSaveVersion }: {
  content: string; onChange: (html: string) => void; placeholder?: string; editable?: boolean;
  onToggleAI?: () => void; isAIOpen?: boolean;
  onToggleVersionHistory?: () => void; isVersionHistoryOpen?: boolean; checkpointCount?: number;
  onSaveVersion?: () => void;
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({ hardBreak: { keepMarks: true } }),
      Underline, FontSize,
      Color.configure({ types: ['textStyle'] }),
      FontFamily.configure({ types: ['textStyle'] }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: true, autolink: true, linkOnPaste: true, protocols: ['http', 'https'],
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer', class: 'note-link' } }),
    ],
    content,
    onUpdate: ({ editor }) => onChangeRef.current(editor.getHTML()),
    editorProps: {
      attributes: { class: 'wysiwyg-editor-content', 'data-placeholder': placeholder || 'Start writing...', autocomplete: 'off', 'data-1p-ignore': '', 'data-lpignore': 'true', 'data-form-type': 'other' },
      handleKeyDown: (view, event) => {
        if (event.key === 'Tab') {
          const { state } = view;
          const { $from } = state.selection;
          const isInList = $from.node($from.depth - 1)?.type.name === 'listItem' || $from.node($from.depth)?.type.name === 'listItem';
          if (!isInList) { event.preventDefault(); view.dispatch(state.tr.insertText('\u00A0\u00A0\u00A0\u00A0')); return true; }
          return false;
        }
        if (event.key === 'Enter' && event.shiftKey) {
          const { state } = view;
          event.preventDefault();
          const hardBreak = state.schema.nodes.hardBreak;
          if (hardBreak) view.dispatch(state.tr.replaceSelectionWith(hardBreak.create()).scrollIntoView());
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content]);

  if (!editor) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ borderBottom: '1px solid var(--color-border, #e5e7eb)', flexShrink: 0, background: 'var(--color-surface)' }}>
        <EditorToolbar editor={editor} onToggleAI={onToggleAI} isAIOpen={isAIOpen} onToggleVersionHistory={onToggleVersionHistory} isVersionHistoryOpen={isVersionHistoryOpen} checkpointCount={checkpointCount} onSaveVersion={onSaveVersion} />
      </div>
      <div className="note-editor-paper-canvas xv-scroll" style={{ flex: 1, overflow: 'auto', background: 'var(--color-surface-muted)' }}>
        <div className="note-editor-paper" style={{
          width: 'calc(100% - 48px)',
          maxWidth: 900,
          margin: '24px auto',
          minHeight: 1056,
          background: 'var(--color-surface)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.08)',
        }}>
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}

// ---------- Main Modal Component ----------

export default function DocEditorModal({ doc, onClose }: DocEditorModalProps) {
  const { token, profile } = useSession();
  const guestId = undefined; // Drive is authenticated-only
  const queryClient = useQueryClient();

  // Save-failure surfacing + unsaved-content buffer (#2671). The editor
  // always opens with the server copy (doc prop); if the previous session's
  // final save failed, the stash is offered back only AFTER the mount GET
  // below resolves, gated on the live etag — see the stash-decision block in
  // that effect.
  const { saveError, reportSaveFailure, handleSaveSuccess, stashUnsavedOnClose, markStashSafeToClear } =
    useDocSaveFailureBuffer(doc.id);
  const { showToast } = useToast();

  const serverTitle = doc.title || '';
  const serverContent = doc.body || doc.content || '';
  // Auto-title derives from the first content line (deriveTitle, in
  // docStashAdjudication.ts). A title is "user-set" if the user manually
  // typed in the title field, or the note already had a real title.
  const isAutoTitle = !serverTitle || serverTitle === 'Untitled' || serverTitle === deriveTitle(serverContent);
  const [titleManuallySet, setTitleManuallySet] = useState(!isAutoTitle);
  const [docTitle, setDocTitle] = useState(serverTitle === 'Untitled' ? '' : serverTitle);
  const [docContent, setDocContent] = useState(() => ensureHtmlLineBreaks(serverContent));
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveStatus, setSaveStatus] = useState('');

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showSaveVersionModal, setShowSaveVersionModal] = useState(false);
  const [versionLabel, setVersionLabel] = useState('');
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>('current');
  const [docCheckpoints, setDocCheckpoints] = useState<DocCheckpoint[]>(doc.checkpoints || []);

  // Refs for autosave
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleDeriveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedContentRef = useRef(docContent);
  const lastSavedTitleRef = useRef(docTitle);
  const currentContentRef = useRef(docContent);
  const currentTitleRef = useRef(docTitle);
  // Initial (server-copy) editor state: the #2671 stash restore in the mount
  // GET effect below only applies while the editor still equals this baseline.
  const initialContentRef = useRef(docContent);
  const initialTitleRef = useRef(docTitle);
  const currentEtagRef = useRef<string | null>(doc._etag || null);
  // ETag of the last version THIS editor successfully saved (seeded from the
  // version it opened on). currentEtagRef keeps the If-Match plumbing role,
  // but its 412-retry advances it from a refetch BEFORE retrying — so after a
  // failed retry it can name a concurrent version this client never saved.
  // The close-time stash is stamped from THIS ref so its "etag of the last
  // successfully saved version" claim stays literally true (#3927 review).
  const lastSavedEtagRef = useRef<string | null>(doc._etag || null);
  // Serializes autosave + close-save so they can't race on a stale etag
  const inFlightSaveRef = useRef<Promise<unknown> | null>(null);

  // We capture `onClose` via a ref so it isn't a dep of the mount GET effect
  // (below, after performAutoSave) — the parent (DriveModule) passes an
  // inline arrow function, which means `onClose` gets a fresh reference on
  // every parent re-render. If we listed it as a dep, every parent re-render
  // (selection change, hover, context menu, etc.) would re-fire that fetch.
  // A spurious 404 from the extra fetches would force-close the modal
  // mid-edit and lose user work.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Update save status display
  useEffect(() => {
    if (!lastSavedAt) return;
    const interval = setInterval(() => setSaveStatus(formatSaveStatus(lastSavedAt)), 10000);
    setSaveStatus(formatSaveStatus(lastSavedAt));
    return () => clearInterval(interval);
  }, [lastSavedAt]);

  // Cancel pending timers if the modal unmounts without going through handleClose (route change, parent conditional render, etc.)
  useEffect(() => () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (titleDeriveTimerRef.current) clearTimeout(titleDeriveTimerRef.current);
  }, []);

  const patchDocWithEtagRetry = useCallback(async (
    patch: Record<string, unknown>,
  ): Promise<void> => {
    const prior = inFlightSaveRef.current;
    if (prior) {
      try { await prior; } catch { /* prior save already logged its error; still attempt ours */ }
    }
    const doPatch = async () => {
      const headers: Record<string, string> = {};
      if (currentEtagRef.current) headers['If-Match'] = currentEtagRef.current;
      return http.patch(`/notes/${doc.id}`, patch, {
        authToken: token ?? undefined,
        guestId: !token ? guestId : undefined,
        headers,
      });
    };
    const op = (async () => {
      try {
        const response = await doPatch();
        if (response.etag) {
          currentEtagRef.current = response.etag;
          lastSavedEtagRef.current = response.etag; // successful PATCH — a version we actually saved
        }
      } catch (err: any) {
        if (err?.status !== 412) throw err;
        console.warn('[DocEditorModal] Version mismatch, fetching latest etag and retrying...');
        const latest = await http.get(`/notes/${doc.id}`, {
          authToken: token ?? undefined,
          guestId: !token ? guestId : undefined,
        });
        // Refetched etag is If-Match plumbing only — NOT a version this
        // client saved; never advance lastSavedEtagRef from it.
        if (latest.etag) currentEtagRef.current = latest.etag;
        const response2 = await doPatch();
        if (response2.etag) {
          currentEtagRef.current = response2.etag;
          lastSavedEtagRef.current = response2.etag; // successful retry PATCH
        }
      }
    })();
    inFlightSaveRef.current = op;
    try {
      await op;
    } finally {
      if (inFlightSaveRef.current === op) {
        inFlightSaveRef.current = null;
      }
    }
  }, [doc.id, token, guestId]);

  // Autosave
  const performAutoSave = useCallback(async (content: string, title: string) => {
    if (content === lastSavedContentRef.current && title === lastSavedTitleRef.current) return;
    setIsSaving(true);
    try {
      const patch: Record<string, unknown> = { body: content };
      if (title.trim()) patch.title = title;
      if (doc.authorId === undefined && profile?.id) {
        patch.authorId = profile.id;
        patch.authorName = profile.name || 'Unknown';
      }
      await patchDocWithEtagRetry(patch);
      lastSavedContentRef.current = content;
      lastSavedTitleRef.current = title;
      setLastSavedAt(new Date());
      handleSaveSuccess();
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('note') });
    } catch (err) {
      console.error('[DocEditorModal] Auto-save failed:', err);
      // #2671: surface the failure instead of silently dropping the edit.
      reportSaveFailure();
    } finally {
      setIsSaving(false);
    }
  }, [doc.authorId, profile, queryClient, patchDocWithEtagRetry, handleSaveSuccess, reportSaveFailure]);

  // #2671: the stash restore in the mount GET effect below schedules its
  // persist through this ref so performAutoSave (whose identity changes with
  // `profile`/`queryClient`) doesn't have to be a dep of that effect —
  // re-firing the GET on parent re-renders is exactly what the onCloseRef
  // comment above guards against.
  const performAutoSaveRef = useRef(performAutoSave);
  useEffect(() => { performAutoSaveRef.current = performAutoSave; }, [performAutoSave]);

  // Auto-checkpoint: create a version checkpoint every 5 minutes of active editing.
  // Keep the latest checkpoints accessible via a ref so the interval doesn't have to
  // re-bind when the array changes.
  const docCheckpointsRef = useRef<DocCheckpoint[]>(docCheckpoints);
  useEffect(() => { docCheckpointsRef.current = docCheckpoints; }, [docCheckpoints]);

  // On mount, fetch the note once to: (a) refresh ETag if absent, preventing
  // a 412 on the first autosave; (b) reconcile checkpoints from the server
  // since the parent's props may be stale (checkpoints written in prior
  // sessions); (c) adjudicate a #2671 unsaved-content stash. Coalesced into
  // a single GET (was previously two parallel calls).
  //
  // The stash decision deliberately waits for this GET (it is NOT applied as
  // initial editor state) so it can be gated on the LIVE etag — a synchronous
  // restore would silently overwrite edits made elsewhere (chat-agent doc
  // edits, another device/tab) between the failed close and this reopen,
  // because the restored body auto-persists ~1.5s later and the PATCH's
  // 412-retry force-refetches the etag. The three-way decision itself
  // (equality-drop / etag-match restore / conflict → recovery checkpoint) is
  // pure and lives in docStashAdjudication.ts — this effect only fetches,
  // calls the adjudicator, and applies the outcome.
  //
  // Each outcome that leaves no rescue pending calls markStashSafeToClear —
  // only then may handleSaveSuccess drop the stash on a successful save. If
  // the GET fails (stash never adjudicated) or the recovery-checkpoint PATCH
  // fails (stash kept for the next open), later successful saves must NOT
  // destroy the buffered copy (#3927 review).
  //
  // If the server returns 404, the note was deleted between the parent
  // rendering and the modal mounting — close the modal instead of leaving
  // the user editing a note that no longer exists. Any other GET failure is
  // non-fatal: no restore happens and the stash stays put for the next open.
  const stashHandledRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    http.get<{ title?: string; body?: string; content?: string; checkpoints?: DocCheckpoint[] }>(`/notes/${doc.id}`, {
      authToken: token ?? undefined,
      guestId: !token ? guestId : undefined,
    }).then((res) => {
      if (cancelled) return;
      if (res.etag && !currentEtagRef.current) currentEtagRef.current = res.etag;
      // Pre-save GET: the live etag IS the last successfully saved version
      // (by anyone), so it may seed the stash stamp — but only when absent;
      // mid-session it advances solely on our own successful PATCHes.
      if (res.etag && !lastSavedEtagRef.current) lastSavedEtagRef.current = res.etag;
      const freshCheckpoints = Array.isArray(res.data?.checkpoints) ? res.data!.checkpoints! : null;
      if (freshCheckpoints) setDocCheckpoints(freshCheckpoints);

      if (stashHandledRef.current) return; // stash already adjudicated this session
      stashHandledRef.current = true;
      const stash = readUnsavedDocStash(doc.id);
      if (!stash) {
        markStashSafeToClear(); // nothing buffered — saves may clear freely
        return;
      }

      const outcome = adjudicateDocStash({
        stash,
        freshData: res.data,
        freshEtag: res.etag ?? null,
        freshCheckpoints,
        currentCheckpoints: docCheckpointsRef.current,
        editorUntouched:
          currentContentRef.current === initialContentRef.current &&
          currentTitleRef.current === initialTitleRef.current,
      });

      // 1. Failure-after-commit: the close PATCH landed server-side — nothing
      // to restore, just drop the stash.
      if (outcome.kind === 'drop') {
        clearUnsavedDocStash(doc.id);
        markStashSafeToClear();
        return;
      }

      // 2. Restore the buffer into the editor.
      if (outcome.kind === 'restore') {
        setTitleManuallySet(outcome.titleManuallySet);
        setDocTitle(outcome.title);
        setDocContent(outcome.body);
        currentTitleRef.current = outcome.title;
        currentContentRef.current = outcome.body;
        // "Last saved" reflects the fresh SERVER copy so the restored buffer
        // counts as unsaved and the next save persists it (which is also
        // what clears the stash).
        lastSavedContentRef.current = outcome.freshBody;
        lastSavedTitleRef.current = outcome.freshTitle;
        // The restored content now flows through the normal save path, so
        // post-restore successful saves SHOULD clear the stash.
        markStashSafeToClear();
        showToast('Restored unsaved changes from your last session', 'info');
        // Persist through the normal debounced-autosave path so the stash
        // clears even if the user closes again without typing. If the server
        // is still failing, this surfaces the save error instead of silently
        // re-buffering. StrictMode-safe: the timer lives in autoSaveTimerRef
        // (cleared by the unmount cleanup above) and is re-armed when this
        // effect re-runs after the simulated unmount.
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(() => {
          performAutoSaveRef.current(currentContentRef.current, currentTitleRef.current);
        }, 1500);
        return;
      }

      // 3. Conflict: preserve the stashed copy as a version-history recovery
      // checkpoint (same PATCH pattern as the auto-checkpoint effect below);
      // never touch the body the user sees.
      patchDocWithEtagRetry({ checkpoints: outcome.updatedCheckpoints }).then(() => {
        if (!cancelled) setDocCheckpoints(outcome.updatedCheckpoints);
        // The checkpoint is durably persisted even if the modal unmounted
        // mid-PATCH — clear the stash regardless of `cancelled` so the next
        // open doesn't write a duplicate recovery checkpoint.
        clearUnsavedDocStash(doc.id);
        markStashSafeToClear();
        showToast('This doc changed since your unsaved edits — they were saved to version history.', 'info');
      }).catch((err) => {
        // Keep the stash — the next open retries the recovery. No toast:
        // nothing is lost, and the body the user sees is untouched. The
        // stash stays NOT-safe-to-clear: a later successful save must not
        // destroy the only copy of the buffered content.
        console.warn('[DocEditorModal] Failed to checkpoint recovered unsaved changes; keeping stash', err);
      });
    }).catch((err: any) => {
      if (cancelled) return;
      if (err?.status === 404) {
        console.warn('[DocEditorModal] note no longer exists — closing', { noteId: doc.id });
        onCloseRef.current();
      }
      // Other errors are non-fatal; autosave's etag-fetch will retry on the
      // first save attempt and surface a real error then. Any stash stays
      // put for the next open.
    });
    return () => { cancelled = true; };
    // patchDocWithEtagRetry's deps are exactly [doc.id, token, guestId],
    // showToast is identity-stable (useCallback([]) in ToastProvider), and
    // markStashSafeToClear is identity-stable (useCallback([]) in the hook),
    // so these deps re-fire the GET only when the doc/auth actually changes.
  }, [doc.id, token, guestId, patchDocWithEtagRetry, showToast, markStashSafeToClear]);

  useEffect(() => {
    if (!doc?.id) return;
    if (selectedVersionId !== 'current') return; // skip while viewing history

    const intervalId = setInterval(async () => {
      const currentContent = currentContentRef.current;
      const currentTitle = currentTitleRef.current;
      if (!currentEtagRef.current) return;

      const hasChanges = currentContent !== lastSavedContentRef.current ||
                         currentTitle !== lastSavedTitleRef.current;
      if (!hasChanges) return;

      const lastCheckpoint = docCheckpointsRef.current[0];
      if (lastCheckpoint && currentContent === lastCheckpoint.content) return;

      const now = new Date();
      const checkpointLabel = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
        + ', ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

      const newCheckpoint: DocCheckpoint = {
        id: `cp-${Date.now()}`,
        label: checkpointLabel,
        content: currentContent,
        createdAt: now.toISOString(),
        author: 'user',
        type: 'auto',
      };
      const updatedCheckpoints = [newCheckpoint, ...docCheckpointsRef.current];

      try {
        await patchDocWithEtagRetry({
          body: currentContent,
          title: currentTitle,
          checkpoints: updatedCheckpoints,
        });
        lastSavedContentRef.current = currentContent;
        lastSavedTitleRef.current = currentTitle;
        setDocCheckpoints(updatedCheckpoints);
        handleSaveSuccess();
      } catch (err) {
        console.error('[DocEditorModal] Auto-checkpoint failed:', err);
        // #2671: status-only — the 5-minute checkpoint cadence shouldn't
        // stack toasts on top of the autosave failure the user already saw.
        reportSaveFailure({ toast: false });
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [doc?.id, selectedVersionId, patchDocWithEtagRetry, handleSaveSuccess, reportSaveFailure]);

  const handleContentChange = useCallback((newContent: string) => {
    setDocContent(newContent);
    currentContentRef.current = newContent;
    // Debounced title auto-derivation for unnamed notes: derive from first line only, and only after 10s of idle
    if (!titleManuallySet) {
      if (titleDeriveTimerRef.current) clearTimeout(titleDeriveTimerRef.current);
      titleDeriveTimerRef.current = setTimeout(() => {
        // Safeguard against a race with handleTitleChange: if the user typed a title since this timer was scheduled, leave it alone
        if (currentTitleRef.current.trim()) return;
        const derived = deriveTitle(currentContentRef.current);
        if (derived) {
          setDocTitle(derived);
          currentTitleRef.current = derived;
        }
      }, 10000);
    }
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave(currentContentRef.current, currentTitleRef.current);
    }, 1500);
  }, [performAutoSave, titleManuallySet]);

  const handleTitleChange = useCallback((newTitle: string) => {
    setTitleManuallySet(newTitle.length > 0);
    setDocTitle(newTitle);
    currentTitleRef.current = newTitle;
    // User is editing the title directly — cancel any pending auto-derive so it can't stomp their input
    if (titleDeriveTimerRef.current) clearTimeout(titleDeriveTimerRef.current);
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave(currentContentRef.current, currentTitleRef.current);
    }, 1500);
  }, [performAutoSave]);

  // Save a checkpoint (version) of the current note
  const handleSaveCheckpoint = useCallback(async (label: string) => {
    if (!label.trim()) return;

    setIsSavingVersion(true);
    try {
      const updatedCheckpoints: DocCheckpoint[] = [{
        id: `cp-${Date.now()}`,
        label: label.trim(),
        content: currentContentRef.current,
        createdAt: new Date().toISOString(),
        author: 'user',
      }, ...docCheckpoints];

      await patchDocWithEtagRetry({ checkpoints: updatedCheckpoints });

      setDocCheckpoints(updatedCheckpoints);
      setShowSaveVersionModal(false);
      setVersionLabel('');
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('note') });
    } catch (err) {
      console.error('[DocEditorModal] Failed to save checkpoint:', err);
      alert('Failed to save version');
    } finally {
      setIsSavingVersion(false);
    }
  }, [queryClient, docCheckpoints, patchDocWithEtagRetry]);

  // Restore a checkpoint
  const handleRestoreCheckpoint = useCallback(async (checkpoint: DocCheckpoint) => {
    const confirmed = confirm(`Restore "${checkpoint.label}"? This will replace the current content.`);
    if (!confirmed) return;

    try {
      const updatedCheckpoints: DocCheckpoint[] = [{
        id: `cp-${Date.now()}`,
        label: `Before restore: ${new Date().toLocaleString()}`,
        content: currentContentRef.current,
        createdAt: new Date().toISOString(),
        author: 'user',
      }, ...docCheckpoints];

      await patchDocWithEtagRetry({
        body: checkpoint.content,
        checkpoints: updatedCheckpoints,
      });

      setDocContent(checkpoint.content);
      currentContentRef.current = checkpoint.content;
      lastSavedContentRef.current = checkpoint.content;
      setDocCheckpoints(updatedCheckpoints);
      setLastSavedAt(new Date());
      handleSaveSuccess();
      setSelectedVersionId('current');
      setShowVersionHistory(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('note') });
    } catch (err) {
      console.error('[DocEditorModal] Failed to restore checkpoint:', err);
      alert('Failed to restore version');
    }
  }, [queryClient, docCheckpoints, patchDocWithEtagRetry, handleSaveSuccess]);

  // Flush autosave on close with auto-checkpoint
  const handleClose = useCallback(async () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (titleDeriveTimerRef.current) clearTimeout(titleDeriveTimerRef.current);

    const currentContent = currentContentRef.current;
    // If the user never named the note, derive title from the first line at close-time only
    let currentTitle = currentTitleRef.current;
    if (!titleManuallySet && !currentTitle.trim()) {
      const derived = deriveTitle(currentContent);
      if (derived) {
        currentTitle = derived;
        currentTitleRef.current = derived;
        setDocTitle(derived);
      }
    }
    const hasChanges = currentContent !== lastSavedContentRef.current || currentTitle !== lastSavedTitleRef.current;

    if (hasChanges) {
      setIsSaving(true);
      try {
        // Read through the ref, not this callback's captured state — the
        // mount effect's conflict path may have prepended a recovery
        // checkpoint (its .then's setDocCheckpoints + the ref-sync effect)
        // after the render this callback closed over. The checkpoint array
        // itself remains last-writer-wins on the server (pre-existing
        // architecture), so a close-save composed while that PATCH is still
        // in flight can still clobber it (#3927 review, F2).
        const existingCheckpoints = docCheckpointsRef.current;
        const lastCheckpoint = existingCheckpoints[0];
        const hasChangesFromLastCheckpoint = !lastCheckpoint || currentContent !== lastCheckpoint.content;

        if (hasChangesFromLastCheckpoint) {
          const now = new Date();
          const checkpointLabel = now.toLocaleDateString('en-US', {
            month: 'long', day: 'numeric'
          }) + ', ' + now.toLocaleTimeString('en-US', {
            hour: 'numeric', minute: '2-digit'
          });

          const newCheckpoint: DocCheckpoint = {
            id: `cp-${Date.now()}`,
            label: checkpointLabel,
            content: currentContent,
            createdAt: now.toISOString(),
            author: 'user',
            type: 'auto',
          };

          const updatedCheckpoints = [newCheckpoint, ...existingCheckpoints];

          await patchDocWithEtagRetry({
            body: currentContent,
            title: currentTitle,
            checkpoints: updatedCheckpoints,
            _forceDocxRender: true,
          });
          console.log('[DocEditorModal] Checkpoint created on close:', checkpointLabel);
        } else {
          await patchDocWithEtagRetry({
            body: currentContent,
            title: currentTitle,
            _forceDocxRender: true,
          });
        }
        lastSavedContentRef.current = currentContent;
        lastSavedTitleRef.current = currentTitle;
        handleSaveSuccess();
        queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('note') });
      } catch (err) {
        console.error('[DocEditorModal] Final save on close failed:', err);
        // #2671: don't silently discard the buffer — stash it to localStorage
        // (stamped with the last successfully saved etag) so the next open of
        // this doc can restore it after checking the live server version.
        // lastSavedEtagRef, NOT currentEtagRef: a failed 412-retry leaves
        // currentEtagRef pointing at a concurrent version this client never
        // saved, and stamping that would let the next open "restore" the
        // stash over someone else's edits (#3927 review, F3).
        stashUnsavedOnClose(currentTitle, currentContent, lastSavedEtagRef.current);
      } finally {
        setIsSaving(false);
      }
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.objects.all });
    onClose();
  }, [onClose, queryClient, patchDocWithEtagRetry, titleManuallySet, handleSaveSuccess, stashUnsavedOnClose]);

  // Click-outside-to-close with mousedown tracking
  const [editorModalRef, handleEditorOverlayClick, handleEditorOverlayMouseDown] = useModalOverlayClick(handleClose);

  // AI panel state
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  const handleSendChat = useCallback(async (directMessage?: string) => {
    const msg = (directMessage || chatInput).trim();
    if (!msg || isChatLoading || !token) return;
    const userMsg = { role: 'user' as const, content: msg };
    const newMessages = [...chatMessages, userMsg];
    setChatMessages(newMessages);
    setChatInput('');
    setIsChatLoading(true);

    try {
      // Give the model the note's HTML so an edit can return formatted HTML back.
      const docHtml = currentContentRef.current;
      const systemPrompt = `You are an inline editing assistant embedded INSIDE one note titled "${currentTitleRef.current}". You can read and edit ONLY this note. You have NO access to other notes, files, Drive, or the workspace — never mention them, never offer to put content elsewhere, and never ask the user where to put something. The answer is always: in THIS note.

THIS note's current content (HTML):
---
${docHtml}
---

How to respond:
- If the user asks you to ADD, CREATE, WRITE, INSERT, fix, rewrite, reword, shorten, expand, or reformat anything (e.g. "create a poem about bitcoin here", "add a section", "fix spelling"), DO IT NOW by returning the COMPLETE updated note as HTML — the existing content plus your change, integrated in place. Do NOT ask where to put it; put it in THIS note. Do NOT say you can't find something or that it doesn't exist yet — just write/add it.
- ONLY when the user asks a pure QUESTION about this note's existing content (e.g. "what is this about?", "summarize this") do you answer in text.
- Never reference other notes or the workspace. Never present options about where to save.

Respond with ONLY a single JSON object and nothing else (no prose, no code fences):
{"action":"edit","html":"<the full updated note as HTML>"}
OR
{"action":"reply","text":"<your answer about this note>"}
Do not use any tools.`;

      const allMessages = newMessages.map(m => ({ role: m.role, content: m.content }));

      // Use the RAW completion endpoint (not the agent /chat) so our note-scoped
      // systemPrompt actually reaches the model — /chat ignores systemPrompt and
      // runs the full general agent (pre-search + tools). The panel's
      // isChatLoading spinner indicates progress while we await.
      const raw = await completeNote(token, { systemPrompt, messages: allMessages, maxTokens: 4000, userId: profile?.id });

      const parsed = parseDocEditResponse(raw);
      if (parsed.action === 'edit') {
        await handleSaveCheckpoint('Before AI edit');
        setDocContent(parsed.html);
        currentContentRef.current = parsed.html;
        // Persist the new body immediately. setDocContent → TipTap
        // setContent does NOT emit onUpdate, so handleContentChange (the only
        // autosave trigger for typed edits) never fires for an AI edit. Without
        // this, the change lives only in memory until an explicit handleClose —
        // a tab close / refresh / route change would lose it.
        await performAutoSave(parsed.html, currentTitleRef.current);
        setChatMessages(prev => [...prev, { role: 'assistant', content: '✓ Applied to your doc.' }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: parsed.text }]);
      }
    } catch (err) {
      console.error('[DocEditorModal] Chat failed:', err);
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong.' }]);
    } finally {
      setIsChatLoading(false);
    }
  }, [chatInput, isChatLoading, token, profile, chatMessages, handleSaveCheckpoint, performAutoSave, setDocContent]);

  // Escape to close. Calls preventDefault() so the module-pane Esc handler
  // (useModulePaneEscapeClose, issue #3938) reliably declines via
  // e.defaultPrevented instead of relying on the overlay-class coincidence —
  // closing this editor never also closes the Drive pane on the same keypress.
  // handleClose flushes the autosave / save-failure buffer, so no unsaved doc
  // edit is lost. Side panels (AI / version history) are dismissed first.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); if (showAIPanel) setShowAIPanel(false); else if (showVersionHistory) { setShowVersionHistory(false); setSelectedVersionId('current'); } else handleClose(); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleClose, showAIPanel, showVersionHistory]);

  const hasSidePanel = showAIPanel || showVersionHistory;

  return createPortal(
    <div className="note-editor-modal-overlay" onMouseDown={handleEditorOverlayMouseDown} onClick={handleEditorOverlayClick}>
      <div
        ref={editorModalRef}
        className={`note-editor-modal ${hasSidePanel ? 'with-ai-panel' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main editor -- stays same size regardless of side panel */}
        <div className="note-editor-main-content" style={{
          display: 'flex', flexDirection: 'column',
          flex: '1',
          background: '#f9fafb',
          width: '100%',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div className="note-editor-modal-header" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 20px 8px', background: 'var(--color-surface)' }}>
            <input
              className="note-editor-title-input"
              value={docTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              placeholder="Untitled"
              autoComplete="off"
              data-1p-ignore=""
              data-lpignore="true"
              data-form-type="other"
              style={{
                flex: 1, fontSize: 20, fontWeight: 600, border: 'none', outline: 'none',
                background: 'transparent', fontFamily: "'DM Sans', sans-serif", color: 'var(--color-text)',
              }}
            />
            {/* saveError takes precedence over saveStatus: the 10s interval
                above re-derives saveStatus from lastSavedAt, so an error
                written into saveStatus would be clobbered (#2671). */}
            <span style={{ fontSize: 11, color: saveError ? 'var(--color-danger)' : 'var(--color-text-faint)', whiteSpace: 'nowrap' }}>
              {isSaving ? 'Saving...' : (saveError ?? saveStatus)}
            </span>
            <button
              onClick={handleClose}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                color: 'var(--color-text-muted)', fontSize: 28, fontWeight: 300, lineHeight: 1,
                alignSelf: 'flex-start', marginTop: -4,
              }}
              title="Close"
            >
              &times;
            </button>
          </div>
          {/* Editor body */}
          <div className="note-editor-modal-body" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {selectedVersionId === 'current' || !showVersionHistory ? (
              <WysiwygEditor
                content={docContent}
                onChange={handleContentChange}
                placeholder="Start writing..."
                editable={true}
                onToggleAI={() => { setShowAIPanel(!showAIPanel); if (!showAIPanel) setShowVersionHistory(false); }}
                isAIOpen={showAIPanel}
                onToggleVersionHistory={() => { setShowVersionHistory(!showVersionHistory); if (!showVersionHistory) { setShowAIPanel(false); setSelectedVersionId('current'); } }}
                isVersionHistoryOpen={showVersionHistory}
                checkpointCount={docCheckpoints.length}
                onSaveVersion={() => setShowSaveVersionModal(true)}
              />
            ) : (
              <DiffView
                oldContent={docCheckpoints.find(c => c.id === selectedVersionId)?.content || ''}
                newContent={docContent}
              />
            )}
          </div>
        </div>

        {/* Side Panel - AI Chat or Version History (fixed position like file viewer) */}
        {hasSidePanel && (
          <div className="note-editor-ai-panel">
            {showVersionHistory ? (
              // Version History Panel
              <>
                {/* Header */}
                {(() => {
                  const allVersions = [
                    { id: 'current', createdAt: new Date().toISOString(), author: 'user', isCurrent: true },
                    ...docCheckpoints.map(cp => ({ ...cp, isCurrent: false }))
                  ];
                  const selectedVersion = allVersions.find(v => v.id === selectedVersionId) || allVersions[0];
                  const versionDate = new Date(selectedVersion.createdAt);
                  const dateTimeLabel = versionDate.toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric'
                  }) + ', ' + versionDate.toLocaleTimeString('en-US', {
                    hour: 'numeric', minute: '2-digit'
                  });

                  return (
                    <div style={{
                      fontSize: 17, fontWeight: 600, color: '#111827', padding: '0 20px',
                      minHeight: 65, background: 'var(--color-surface)', display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', borderBottom: '1px solid var(--color-border)', flexShrink: 0,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, fontWeight: 500, color: 'var(--color-text)' }}>
                        <button
                          onClick={() => { setShowVersionHistory(false); setSelectedVersionId('current'); }}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--color-text-muted)', transition: 'color 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
                          title="Close version history"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                          </svg>
                        </button>
                        <span>{dateTimeLabel}</span>
                      </div>
                      {selectedVersionId !== 'current' && (
                        <button
                          onClick={() => {
                            const checkpoint = docCheckpoints.find(cp => cp.id === selectedVersionId);
                            if (checkpoint) handleRestoreCheckpoint(checkpoint);
                          }}
                          style={{
                            background: 'var(--color-primary)', color: 'white', border: 'none',
                            borderRadius: 6, padding: '8px 16px', fontSize: 14, fontWeight: 500,
                            cursor: 'pointer', transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-primary-hover)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-primary)'; }}
                        >
                          Restore
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Version List */}
                <div className="xv-scroll" style={{ flex: 1, overflowY: 'auto', padding: 12, background: 'var(--color-surface)' }}>
                  {(() => {
                    const allVersions = [
                      { id: 'current', createdAt: new Date().toISOString(), author: 'user', isCurrent: true },
                      ...docCheckpoints.map(cp => ({ ...cp, isCurrent: false }))
                    ];

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {allVersions.map((version) => {
                          const isSelected = selectedVersionId === version.id;
                          const versionDate = new Date(version.createdAt);

                          return (
                            <div
                              key={version.id}
                              onClick={() => setSelectedVersionId(version.id)}
                              style={{
                                padding: 12, border: '1px solid #e5e7eb', borderRadius: 8,
                                background: isSelected ? '#f0f9ff' : 'white',
                                transition: 'all 0.15s', cursor: 'pointer',
                                boxShadow: isSelected ? '0 2px 8px rgba(59, 130, 246, 0.15)' : 'none',
                              }}
                              onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'; } }}
                              onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; } }}
                            >
                              {(() => {
                                const dateStr = `${versionDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}, ${versionDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
                                const cp = version as DocCheckpoint;
                                const row = describeVersionRow(
                                  { dateStr, label: cp.label, author: cp.author, type: cp.type, isCurrent: version.isCurrent },
                                  profile?.name,
                                );
                                return (
                                  <>
                                    <div style={{
                                      fontSize: 14, fontWeight: isSelected ? 600 : 500,
                                      color: isSelected ? '#111827' : '#374151', marginBottom: 4,
                                      display: 'flex', alignItems: 'center', gap: 6,
                                    }}>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.heading}</span>
                                      {row.isAi && (
                                        <span style={{
                                          fontSize: 10, fontWeight: 600, color: 'var(--color-primary)',
                                          background: 'var(--color-primary-muted)', borderRadius: 4, padding: '1px 6px', flexShrink: 0,
                                        }}>AI</span>
                                      )}
                                    </div>
                                    {row.timestamp && (
                                      <div style={{ fontSize: 12, color: isSelected ? '#6b7280' : '#9ca3af', marginBottom: 2 }}>{row.timestamp}</div>
                                    )}
                                    <div style={{ fontSize: 13, color: isSelected ? '#374151' : '#6b7280', marginBottom: version.isCurrent ? 4 : 0 }}>
                                      {row.byline}
                                    </div>
                                    {version.isCurrent && (
                                      <div style={{ fontSize: 13, color: '#059669', fontWeight: 500 }}>
                                        Current version
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </>
            ) : (
              // AI Chat Panel
              <>
                {/* Header */}
                <div style={{
                  fontSize: 15, fontWeight: 600, color: '#111827', padding: '16px 20px',
                  background: 'var(--color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  borderBottom: '1px solid var(--color-border)',
                }}>
                  <span style={{ letterSpacing: '0.15em', fontWeight: 600 }}>Editing: {docTitle || 'this doc'}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {chatMessages.length > 0 && (
                      <button onClick={() => setChatMessages([])}
                        style={{ padding: '6px 12px', background: 'transparent', color: 'var(--color-text-muted)',
                          border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                        Clear
                      </button>
                    )}
                    <button onClick={() => setShowAIPanel(false)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                        color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', borderRadius: 4 }}
                      title="Close AI panel">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Messages */}
                <div ref={chatMessagesRef} className="xv-scroll" style={{
                  flex: 1, overflowY: 'auto', padding: 12, display: 'flex',
                  flexDirection: 'column', gap: 12, background: 'var(--color-surface)',
                }}>
                  {chatMessages.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--color-text-subtle)', fontSize: 14, lineHeight: 1.5 }}>
                      Ask me to fix spelling, rewrite, shorten, or summarize — I'll edit <strong>this doc</strong> directly.
                    </div>
                  ) : (
                    chatMessages.map((msg, idx) => (
                      <div key={idx} style={{
                        padding: '10px 12px', borderRadius: 12, maxWidth: '90%',
                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        background: msg.role === 'user' ? 'var(--color-surface-hover)' : 'var(--color-surface)',
                        color: 'var(--color-text-subtle)', fontSize: 15, lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {msg.content}
                      </div>
                    ))
                  )}
                  {isChatLoading && (
                    <div style={{
                      padding: '10px 12px', borderRadius: 12, background: 'var(--color-surface-hover)',
                      alignSelf: 'flex-start', color: 'var(--color-text-faint)', fontSize: 13,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                      </svg>
                      Thinking...
                    </div>
                  )}
                </div>

                {/* Input */}
                <div style={{ padding: '16px 20px', flexShrink: 0, background: 'var(--color-surface)' }}>
                  <div style={{
                    display: 'flex', border: '1px solid var(--color-border-strong)', borderRadius: 12,
                    background: 'var(--color-surface)', overflow: 'hidden',
                  }}>
                    <textarea
                      value={chatInput}
                      onChange={(e) => { setChatInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                      placeholder="Fix spelling, rewrite, summarize… (acts on this doc)"
                      disabled={isChatLoading}
                      style={{
                        flex: 1, padding: '12px 16px', fontSize: 15, lineHeight: 1.5,
                        border: 'none', outline: 'none', background: 'transparent',
                        fontFamily: 'inherit', resize: 'none', minHeight: 44, maxHeight: 120,
                      }}
                    />
                    <button
                      onClick={() => handleSendChat()}
                      disabled={!chatInput.trim() || isChatLoading}
                      style={{
                        padding: '0 16px', background: 'none', border: 'none', cursor: 'pointer',
                        color: chatInput.trim() ? 'var(--color-primary)' : 'var(--color-text-faint)',
                        fontSize: 18, flexShrink: 0,
                      }}
                    >
                      &uarr;
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Save Version Modal */}
      {showSaveVersionModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10001,
        }} onClick={() => setShowSaveVersionModal(false)}>
          <div style={{
            background: 'var(--color-surface)', borderRadius: 12, padding: 24, width: 400,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>Save Version</h3>
            <input
              value={versionLabel}
              onChange={(e) => setVersionLabel(e.target.value)}
              placeholder="Version label (e.g., Draft 1, Final)"
              onKeyDown={(e) => { if (e.key === 'Enter' && versionLabel.trim()) handleSaveCheckpoint(versionLabel); }}
              autoFocus
              style={{
                width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db',
                borderRadius: 8, outline: 'none', marginBottom: 16, boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSaveVersionModal(false)}
                style={{
                  padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 500,
                  background: 'var(--color-action-cancel-bg)', border: '1px solid var(--color-action-cancel-border)',
                  color: 'var(--color-action-cancel-text)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveCheckpoint(versionLabel)}
                disabled={!versionLabel.trim() || isSavingVersion}
                style={{
                  padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 500,
                  background: 'var(--color-primary)', border: 'none', color: 'white',
                  cursor: !versionLabel.trim() || isSavingVersion ? 'not-allowed' : 'pointer',
                  opacity: !versionLabel.trim() || isSavingVersion ? 0.5 : 1,
                }}
              >
                {isSavingVersion ? 'Saving...' : 'Save Version'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
